import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * Comfort controls — one place for every "how the page feels" preference.
 * These exist for everyone: text scale, line height, a reading ruler, motion
 * and sound. Preferences persist locally and apply instantly.
 */

export type TextScale = 's' | 'm' | 'l';
export type LineSpace = 'cozy' | 'normal' | 'airy';
export type RulerMode = 'off' | 'line' | 'wide';
export type MotionPref = 'system' | 'reduce';
export type AppTheme = 'paper' | 'oled' | 'dusk';

export interface ComfortSettings {
  textScale: TextScale;
  lineSpace: LineSpace;
  ruler: RulerMode;
  motion: MotionPref;
  sound: boolean;
  dailyGoalXp: number;
  focusPresetMinutes: number;
}

const DEFAULTS: ComfortSettings = {
  textScale: 'm',
  lineSpace: 'normal',
  ruler: 'off',
  motion: 'system',
  sound: false,
  dailyGoalXp: 40,
  focusPresetMinutes: 25
};

const STORAGE_KEY = 'cp-ultimate-comfort';

interface ComfortContextValue {
  comfort: ComfortSettings;
  update: <K extends keyof ComfortSettings>(key: K, value: ComfortSettings[K]) => void;
  reduceMotion: boolean;
}

const ComfortContext = createContext<ComfortContextValue | null>(null);

function loadSettings(): ComfortSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    return { ...DEFAULTS, ...(JSON.parse(raw) as Partial<ComfortSettings>) };
  } catch {
    return DEFAULTS;
  }
}

export function ComfortProvider({ children }: { children: ReactNode }) {
  const [comfort, setComfort] = useState<ComfortSettings>(loadSettings);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.textScale = comfort.textScale;
    root.dataset.lineSpace = comfort.lineSpace;
    root.dataset.ruler = comfort.ruler;
    root.dataset.motion = comfort.motion;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(comfort));
  }, [comfort]);

  const systemReduce = useMemo(
    () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches,
    []
  );
  const reduceMotion = comfort.motion === 'reduce' || (comfort.motion === 'system' && systemReduce);

  useEffect(() => {
    document.documentElement.dataset.reduceMotion = reduceMotion ? 'true' : 'false';
  }, [reduceMotion]);

  const value = useMemo<ComfortContextValue>(() => ({
    comfort,
    reduceMotion,
    update: (key, setting) => setComfort((current) => ({ ...current, [key]: setting }))
  }), [comfort, reduceMotion]);

  return <ComfortContext.Provider value={value}>{children}</ComfortContext.Provider>;
}

export function useComfort(): ComfortContextValue {
  const value = useContext(ComfortContext);
  if (!value) throw new Error('useComfort must be used inside ComfortProvider.');
  return value;
}
